(ns arthur.domain.ring
  "Operations on an ordered ring of points.

  A ring here is a closed traversal: a vector of points where slot k means the
  same thing on every frame of a shot. That is what makes temporal
  correspondence possible at all, so nothing in here is allowed to reorder,
  insert or adaptively decimate — every function is index-preserving or returns
  slot positions.")

(defn subsample-slots
  "Pick `n` slots from a ring of `len` by even spacing. Returns RING POSITIONS,
  not landmark ids: positions are the vertex identity downstream, and mapping ids
  back to positions with indexOf would silently pick the wrong slot if a table
  ever repeated an id.

  Fixed indices, never adaptive decimation: the vertex at slot k means the same
  thing on every frame of the shot.

  WHICH CARDINAL POSITIONS SURVIVE DEPENDS ON n, and not merely on n being even.
  On a 20-point ring the corners at 0 and 10 are kept by every even n, but the lip
  centres at 5 and 15 are kept only when n is a multiple of 4: n = 6, 10, 14 and
  18 all drop them. So nothing may assume a named slot is still in the output —
  the aperture pair least of all. A signal that needs those two landmarks reads
  them from a measurement, not from a subsampled ring."
  [len n]
  (mapv (fn [k] (mod (js/Math.round (/ (* k len) n)) len)) (range n)))

(defn subsample-ring
  "`subsample-slots` applied to a table, yielding landmark ids."
  [ring n]
  (mapv #(nth ring %) (subsample-slots (count ring) n)))

(defn offset-ring
  "Push a ring outward from its centroid by a FIXED distance, not by a scale
  factor.

  Scaling collapses with the shape: a shut eyelid scaled by 1.1 is still a shut
  eyelid, so the lash line - the only thing left to draw when the eye is closed
  - would vanish exactly on the frames where it is the whole drawing. A fixed
  radial offset gives a band of roughly constant thickness that survives the
  ring going degenerate, and it keeps a star-shaped ring simple, which
  docs/design.md requires of every cut part."
  [pts d]
  (if (or (nil? d) (zero? d))
    pts
    (let [n  (count pts)
          cx (/ (reduce + (map :x pts)) n)
          cy (/ (reduce + (map :y pts)) n)]
      (mapv (fn [p]
              (let [dx (- (:x p) cx)
                    dy (- (:y p) cy)
                    m  (js/Math.hypot dx dy)]
                ;; A vertex sitting exactly on the centroid has no outward
                ;; direction. Leave it where it is rather than emitting NaN and
                ;; poisoning the whole ring.
                (if (< m 1e-9)
                  {:x (:x p) :y (:y p)}
                  {:x (+ (:x p) (* (/ dx m) d))
                   :y (+ (:y p) (* (/ dy m) d))})))
            pts))))

(defn- orient
  "Sign of the cross product (p->q) x (p->r): which side of pq the point r is on."
  [p q r]
  (js/Math.sign (- (* (- (:x q) (:x p)) (- (:y r) (:y p)))
                   (* (- (:y q) (:y p)) (- (:x r) (:x p))))))

(defn segments-cross?
  "True when ab and cd cross properly. Collinear and touching cases are
  deliberately NOT crossings: adjacent ring edges share an endpoint, and a
  degenerate ring — the shut eyelid — has collinear ones. Reporting those would
  make the simplicity assertion fire on exactly the shapes it has to allow."
  [a b c d]
  (let [o1 (orient a b c) o2 (orient a b d)
        o3 (orient c d a) o4 (orient c d b)]
    (and (not= o1 o2) (not= o3 o4)
         (not (zero? o1)) (not (zero? o2))
         (not (zero? o3)) (not (zero? o4)))))

(defn self-intersections
  "Every pair of non-adjacent edges of the closed ring that cross, as [i j].

  This exists because \"fixed topology\" is load-bearing: because hold parts CUT
  between poses rather than interpolating, a ring whose vertex order is wrong
  self-intersects and renders as blocks meeting at corners. It is invisible at
  odd vertex counts and obvious at even ones, so it needs an assertion rather
  than an eyeball."
  [pts]
  (let [n (count pts)
        at (fn [i] (nth pts (mod i n)))]
    (vec
     (for [i (range n)
           j (range (inc i) n)
           :when (and (not= (mod (inc j) n) i)
                      (not= (mod (inc i) n) j))
           :when (segments-cross? (at i) (at (inc i)) (at j) (at (inc j)))]
       [i j]))))

(defn simple?
  "True when no pair of non-adjacent edges crosses."
  [pts]
  (empty? (self-intersections pts)))
